import { useCallback, useEffect, useRef, useState } from "react";
import { Button, DatePicker, Empty, Radio, Spin, Tag } from "antd";
import {
  CalendarOutlined,
  RadarChartOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { Column } from "@antv/g2plot";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import isBetween from "dayjs/plugin/isBetween";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isEmpty from "../validations/is-empty";
import openNotification from "../components/Notifications/openNotification";
import apiClient from "../actions/api";
import { getCountryShortName } from "../config/countries";
import getDate from "../untils/getDate";

// import style
import "../style/chartpage.scss";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isBetween);
dayjs.extend(customParseFormat);

const weekFormat = "YYYY-MM-DD";

const ChartPage = ({ title }) => {
  // set title
  document.title = title;

  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);

  const perBiddersChartRef = useRef(null);
  const perBiddersChartcolumnRef = useRef(null);

  const perWeekChartRef = useRef(null);
  const perWeekChartcolumnRef = useRef(null);

  const perWeekInterviewChartRef = useRef(null);
  const perWeekInterviewChartcolumnRef = useRef(null);

  const perInterviewersChartRef = useRef(null);
  const perInterviewersChartcolumnRef = useRef(null);

  const [searchStartDate, setSearchStartDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true).startOf("week"),
  );
  const [searchEndDate, setSearchEndDate] = useState(
    dayjs(getDate(dayjs())).tz(timezoneMode.timezoneMode, true).endOf("week"),
  );

  const [perBiddersChartLoading, setPerBiddersChartLoading] = useState(false);
  const [statisticPerBiddersData, setStatisticPerBiddersData] = useState([]);
  const [statisticPerWeekData, setStatisticPerWeekData] = useState([]);
  const [statisticPerInterviewWeekData, setStatisticPerInterviewWeekData] =
    useState([]);
  const [statisticPerInterviewersData, setStatisticPerInterviewersData] =
    useState([]);

  const [chartViewMode, setChartViewMode] = useState("Bid");

  const radioOptions = [
    { label: "Bid", value: "Bid", className: "label-1" },
    { label: "Interview", value: "Interview", className: "label-2" },
  ];

  const customWeekStartEndFormat = (value) =>
    `${dayjs(value)
      .tz(timezoneMode.timezoneMode, true)
      .startOf("week")
      .format(weekFormat)} ~ ${dayjs(value)
      .tz(timezoneMode.timezoneMode, true)
      .endOf("week")
      .format(weekFormat)}`;

  const safeDestroyChart = (chartRef) => {
    if (chartRef.current) {
      try {
        chartRef.current.destroy();
      } catch (e) {
        console.warn("Chart already removed:", e);
      } finally {
        chartRef.current = null;
      }
    }
  };

  useEffect(() => {
    if (chartViewMode === "Interview") {
      safeDestroyChart(perBiddersChartcolumnRef);
      safeDestroyChart(perWeekChartcolumnRef);
    } else {
      safeDestroyChart(perWeekInterviewChartcolumnRef);
      safeDestroyChart(perInterviewersChartcolumnRef);
    }
  }, [chartViewMode]);

  useEffect(() => {
    safeDestroyChart(perBiddersChartcolumnRef);
    if (statisticPerBiddersData.length && perBiddersChartRef.current) {
      perBiddersChartcolumnRef.current = new Column(
        perBiddersChartRef.current,
        {
          data: statisticPerBiddersData,
          xField: "bid_date",
          yField: "bid_count",
          seriesField: "username",
          isGroup: true,
          columnStyle: {
            radius: [5, 5, 0, 0],
          },
        },
      );

      perBiddersChartcolumnRef.current.render();
    }
    return () => safeDestroyChart(perBiddersChartcolumnRef);
  }, [statisticPerBiddersData, chartViewMode]);

  useEffect(() => {
    safeDestroyChart(perWeekChartcolumnRef);
    if (statisticPerWeekData.length && perWeekChartRef.current) {
      perWeekChartcolumnRef.current = new Column(perWeekChartRef.current, {
        data: statisticPerWeekData,
        xField: "username",
        yField: "bid_count",
        seriesField: "bid_date",
        isGroup: true,
        columnStyle: {
          radius: [5, 5, 0, 0],
        },
      });

      perWeekChartcolumnRef.current.render();
    }
    return () => safeDestroyChart(perWeekChartcolumnRef);
  }, [statisticPerWeekData, chartViewMode]);

  useEffect(() => {
    safeDestroyChart(perWeekInterviewChartcolumnRef);
    if (
      statisticPerInterviewWeekData.length &&
      perWeekInterviewChartRef.current
    ) {
      perWeekInterviewChartcolumnRef.current = new Column(
        perWeekInterviewChartRef.current,
        {
          data: statisticPerInterviewWeekData,
          xField: "start_date",
          yField: "interview_count",
          seriesField: "username",
          isGroup: true,
          columnStyle: {
            radius: [5, 5, 0, 0],
          },
        },
      );

      perWeekInterviewChartcolumnRef.current.render();
    }
    return () => safeDestroyChart(perWeekInterviewChartcolumnRef);
  }, [statisticPerInterviewWeekData, chartViewMode]);

  useEffect(() => {
    safeDestroyChart(perInterviewersChartcolumnRef);
    if (
      statisticPerInterviewersData.length &&
      perInterviewersChartRef.current
    ) {
      perInterviewersChartcolumnRef.current = new Column(
        perInterviewersChartRef.current,
        {
          data: statisticPerInterviewersData,
          xField: "username",
          yField: "interview_count",
          seriesField: "start_date",
          isGroup: true,
          columnStyle: {
            radius: [5, 5, 0, 0],
          },
        },
      );

      perInterviewersChartcolumnRef.current.render();
    }
    return () => safeDestroyChart(perInterviewersChartcolumnRef);
  }, [statisticPerInterviewersData, chartViewMode]);

  const loadStatistics = useCallback(async () => {
    const originalStartTime = `${dayjs(searchStartDate).year()}-${String(
      dayjs(searchStartDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchStartDate).date()).padStart(
      2,
      "0",
    )} 00:00:00`;

    const timeStartInUTC = dayjs(originalStartTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeStartInGMTPlus9 = timeStartInUTC.tz(
      timezoneMode.timezoneMode,
      true,
    );

    const originalEndTime = `${dayjs(searchEndDate).year()}-${String(
      dayjs(searchEndDate).month() + 1,
    ).padStart(2, "0")}-${String(dayjs(searchEndDate).date()).padStart(
      2,
      "0",
    )} 23:59:59`;
    const timeEndInUTC = dayjs(originalEndTime).tz(
      timezoneMode.timezoneMode,
      true,
    );
    const timeEndInGMTPlus9 = timeEndInUTC.tz(timezoneMode.timezoneMode, true);

    const params = {
      searchStartDate: timeStartInGMTPlus9,
      searchEndDate: timeEndInGMTPlus9,
    };

    setPerBiddersChartLoading(true);
    setStatisticPerWeekData([]);
    setStatisticPerBiddersData([]);
    setStatisticPerInterviewWeekData([]);
    setStatisticPerInterviewersData([]);

    await apiClient
      .post("/bids/get-statistic", params)
      .then(async (res) => {
        if (isEmpty(res.data)) {
          setStatisticPerBiddersData([]);
          setStatisticPerWeekData([]);
          setStatisticPerInterviewWeekData([]);
          setStatisticPerInterviewersData([]);
          setPerBiddersChartLoading(false);
        } else {
          const countsMap = new Map();
          const interviewCoundsMap = new Map();

          await res.data.result.forEach((item) => {
            if (!isEmpty(item.user)) {
              const username = item.user.name;
              const jobType = `(${getCountryShortName(item.user.job_type)})`;

              const key = `${username}/${getDate(item.date)}/${jobType}`;
              countsMap.set(key, (countsMap.get(key) || 0) + 1);
            }
          });

          await res.data.interviewResult
            .sort((a, b) => new Date(a.start_date) - new Date(b.start_date))
            .forEach((item) => {
              if (!isEmpty(item.user)) {
                const username = item.user.name;
                const jobType = `(${getCountryShortName(item.user.job_type)})`;

                if (item?.status !== 0 && item?.status !== 6) {
                  const interviewKey = `${username}/${getDate(item.start_date)}/${jobType}`;
                  interviewCoundsMap.set(
                    interviewKey,
                    (interviewCoundsMap.get(interviewKey) || 0) + 1,
                  );
                }
              }
            });

          const tempData = [];
          const interviewTempData = [];
          let index = 1;
          let interviewIndex = 1;

          for (const [key, bidCount] of countsMap.entries()) {
            const [username, date, jobType] = key.split("/");

            tempData.push({
              key: index++,
              username: username + " " + jobType,
              bid_date: date,
              bid_count: bidCount,
            });
          }

          for (const [key, interviewCount] of interviewCoundsMap.entries()) {
            const [username, start_date, jobType] = key.split("/");

            interviewTempData.push({
              key: interviewIndex++,
              username: username + " " + jobType,
              start_date: start_date,
              interview_count: interviewCount,
            });
          }

          setStatisticPerBiddersData(tempData);
          setStatisticPerWeekData(tempData);
          setStatisticPerInterviewWeekData(interviewTempData);
          setStatisticPerInterviewersData(interviewTempData);
          setPerBiddersChartLoading(false);
        }
      })
      .catch((err) => {
        if (!isEmpty(err?.response?.data?.errors)) {
          err.response.data.errors.forEach((item) => {
            openNotification("error", "Validation Error!", item.msg);
          });
        }
        setPerBiddersChartLoading(false);
        setStatisticPerWeekData([]);
        setStatisticPerBiddersData([]);
        setStatisticPerInterviewWeekData([]);
        setStatisticPerInterviewersData([]);
      });
  }, [searchStartDate, searchEndDate, timezoneMode.timezoneMode]);

  useEffect(() => {
    loadStatistics();
  }, [loadStatistics]);

  return (
    <div className="chartPageSection">
      <div className="chart-page-heading">
        <div>
          <h2 className="title">
            <span className="chart-title-icon">
              <RadarChartOutlined />
            </span>
            Team Statistic
          </h2>
          <p>Explore weekly performance across bids and interviews.</p>
        </div>
        <Tag>{chartViewMode} statistic</Tag>
      </div>

      <div className="per-bidders-statistic-section">
        {perBiddersChartLoading && (
          <div
            className="statistics-view-loading"
            role="status"
            aria-live="polite"
          >
            <div className="statistics-view-loading-indicator">
              <Spin size="large" />
              <span>Loading team statistic...</span>
            </div>
          </div>
        )}
        <div className="search-section">
          <div className="filter-title">
            <div>
              <CalendarOutlined /> Reporting period
            </div>
            <span>Select a week and statistic view</span>
          </div>
          <div className="field">
            <div className="label">Week</div>
            <DatePicker
              onChange={(date, dateString) => {
                if (isEmpty(dateString)) {
                  setSearchStartDate(
                    dayjs(getDate(dayjs()))
                      .tz(timezoneMode.timezoneMode, true)
                      .startOf("week"),
                  );
                  setSearchEndDate(
                    dayjs(getDate(dayjs()))
                      .tz(timezoneMode.timezoneMode, true)
                      .endOf("week"),
                  );
                } else {
                  setSearchStartDate(
                    dayjs(date)
                      .tz(timezoneMode.timezoneMode, true)
                      .startOf("week"),
                  );
                  setSearchEndDate(
                    dayjs(date)
                      .tz(timezoneMode.timezoneMode, true)
                      .endOf("week"),
                  );
                }
              }}
              value={dayjs(searchStartDate)
                .tz(timezoneMode.timezoneMode, true)
                .startOf("week")}
              format={customWeekStartEndFormat}
              picker="week"
              disabled={perBiddersChartLoading}
            />
          </div>
          <div className="field">
            <div className="label">&nbsp;</div>
            <Button
              type="primary"
              onClick={loadStatistics}
              loading={perBiddersChartLoading}
              icon={<SearchOutlined />}
            >
              Apply
            </Button>
          </div>
          <div
            style={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-end",
            }}
          >
            <Radio.Group
              options={radioOptions}
              onChange={(e) => setChartViewMode(e.target.value)}
              value={chartViewMode}
              optionType="button"
              buttonStyle="solid"
            />
          </div>
        </div>
        {chartViewMode === "Bid" ? (
          <>
            <div className="each-chart-view">
              <div className="chart-card-heading">
                <div>
                  <h3 className="sub-title">Weekly bids</h3>
                  <span>Bid volume across the selected week</span>
                </div>
              </div>
              <div className="per-bidders-statistic-chart-view">
                {isEmpty(statisticPerWeekData) ? (
                  <div
                    key="weekly-bids-empty"
                    className={`per-bidders-statistic-chart-empty ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                  >
                    <Empty />
                  </div>
                ) : (
                  <div
                    key="weekly-bids-chart"
                    className={`per-bidders-statistic-chart ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                    ref={perWeekChartRef}
                  />
                )}
              </div>
            </div>
            <div className="each-chart-view">
              <div className="chart-card-heading">
                <div>
                  <h3 className="sub-title">Bids by teammate</h3>
                  <span>Contribution by individual bidder</span>
                </div>
              </div>
              <div className="per-bidders-statistic-chart-view">
                {isEmpty(statisticPerBiddersData) ? (
                  <div
                    key="teammate-bids-empty"
                    className={`per-bidders-statistic-chart-empty ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                  >
                    <Empty />
                  </div>
                ) : (
                  <div
                    key="teammate-bids-chart"
                    className={`per-bidders-statistic-chart ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                    ref={perBiddersChartRef}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="each-chart-view">
              <div className="chart-card-heading">
                <div>
                  <h3 className="sub-title">Interviews by teammate</h3>
                  <span>Interview outcomes by bidder</span>
                </div>
              </div>
              <div className="per-bidders-statistic-chart-view">
                {isEmpty(statisticPerInterviewersData) ? (
                  <div
                    key="teammate-interviews-empty"
                    className={`per-bidders-statistic-chart-empty ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                  >
                    <Empty />
                  </div>
                ) : (
                  <div
                    key="teammate-interviews-chart"
                    className={`per-bidders-statistic-chart ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                    ref={perInterviewersChartRef}
                  />
                )}
              </div>
            </div>
            <div className="each-chart-view">
              <div className="chart-card-heading">
                <div>
                  <h3 className="sub-title">Weekly interviews</h3>
                  <span>Interview activity across the selected week</span>
                </div>
              </div>
              <div className="per-bidders-statistic-chart-view">
                {isEmpty(statisticPerInterviewWeekData) ? (
                  <div
                    key="weekly-interviews-empty"
                    className={`per-bidders-statistic-chart-empty ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                  >
                    <Empty />
                  </div>
                ) : (
                  <div
                    key="weekly-interviews-chart"
                    className={`per-bidders-statistic-chart ${
                      themeMode.themeMode ? "dark-background-color" : ""
                    }`}
                    ref={perWeekInterviewChartRef}
                  />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChartPage;
